import type Indicator from '../../../app/builder/condition/indicators/types/Indicator'
import {
  CompositeExpr,
  CompositeNode,
  CompositeOutput,
  CompositeRef,
} from '../../../types/composite'
import { isNodeRef } from './refs'

type NodeScale = 'price' | 'other'

const PRICE_SOURCES = new Set(['open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4'])

// Scale-preserving (overlay-class) indicators output the scale of their input
// — SMA of close is price-scaled, but SMA of RSI is RSI-scaled. Everything
// else (RSI, stochastic, ATR, MACD, …) produces its own scale.
const SCALE_PRESERVING = new Set([
  'simpleMovingAverage',
  'exponentialMovingAverage',
  'bollingerBands',
  'keltnerChannel',
  'donchianChannel',
  'movingAverageEnvelope',
  'ichimokuCloud',
  'parabolicSAR',
])

/**
 * Conservative scale inference for the builder's default: 'overlay' only when
 * every exposed output provably stays on the price scale (price sources
 * flowing through scale-preserving indicators and price-safe math); anything
 * ambiguous is 'oscillator'. The author can always override.
 */
export const inferCompositeScale = (
  nodes: CompositeNode[],
  outputs: CompositeOutput[],
  getIndicatorByValue?: (value: string) => Indicator | undefined
): 'overlay' | 'oscillator' => {
  void getIndicatorByValue // reserved for finer-grained rules later
  const scales = new Map<string, NodeScale>()

  const refScale = (ref: CompositeRef): NodeScale =>
    isNodeRef(ref)
      ? (scales.get(ref.node) ?? 'other')
      : PRICE_SOURCES.has(ref.source)
        ? 'price'
        : 'other'

  const exprScale = (expr: CompositeExpr): NodeScale | 'const' => {
    if (expr.t === 'const') return 'const'
    if (expr.t === 'ref') return refScale(expr.ref)
    switch (expr.op) {
      case '>':
      case '<':
      case '>=':
      case '<=':
      case 'and':
      case 'or':
      case 'not':
        return 'other' // 1/0 series
      case '+':
      case '-':
      case 'abs':
      case 'neg':
      case 'min':
      case 'max': {
        // Price-safe when every non-constant operand is price-scaled
        const argScales = expr.args.map(exprScale)
        const nonConst = argScales.filter((s) => s !== 'const')
        return nonConst.length > 0 && nonConst.every((s) => s === 'price') ? 'price' : 'other'
      }
      case '*':
      case '/': {
        // price × constant stays price; price × price (or anything mixed)
        // doesn't. Division is not commutative: const ÷ price is a reciprocal,
        // so the price operand must be the numerator to stay price-scaled.
        const argScales = expr.args.map(exprScale)
        const priceCount = argScales.filter((s) => s === 'price').length
        const constCount = argScales.filter((s) => s === 'const').length
        if (priceCount !== 1 || priceCount + constCount !== argScales.length) return 'other'
        return expr.op === '/' && argScales[0] !== 'price' ? 'other' : 'price'
      }
      default:
        return 'other'
    }
  }

  for (const node of nodes) {
    if (node.kind === 'indicator') {
      if (!SCALE_PRESERVING.has(node.name)) {
        scales.set(node.id, 'other')
      } else {
        // Chart-fed (no input ref) means a price-field param → price scale
        scales.set(node.id, node.input ? refScale(node.input) : 'price')
      }
    } else {
      const scale = exprScale(node.expr)
      scales.set(node.id, scale === 'const' ? 'other' : scale)
    }
  }

  const allPrice = outputs.length > 0 && outputs.every((output) => refScale(output.ref) === 'price')
  return allPrice ? 'overlay' : 'oscillator'
}
